import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatEmployeeRange } from '@/utils/formatters';
import { BookmarkIcon, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CompanyTableViewProps {
  companies: any[];
  onCompanyClick: (index: number) => void;
  onEditClick: (slug: string) => void;
  scrapbookEntries: any[];
}

const CompanyTableView = ({
  companies,
  onCompanyClick,
  onEditClick,
  scrapbookEntries
}: CompanyTableViewProps) => {
  // Create a map of company slugs that are in the scrapbook
  const scrapbookMap = new Map(
    scrapbookEntries.map(entry => [entry.company_slug, true])
  );

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <TableHead>Company</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Employees</TableHead>
            <TableHead>Modalities</TableHead>
            <TableHead className="w-[80px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {companies.map((company, index) => {
            const isInScrapbook = scrapbookMap.has(company.slug);
            
            // Ensure tags is always an array
            const tags = Array.isArray(company.tags) ? company.tags : [];
            
            // Format tags for display
            const formattedTags = tags.map(tag => {
              if (typeof tag === 'string') return tag;
              if (tag?.name) return tag.name;
              if (tag?.tag?.name) return tag.tag.name;
              return '';
            }).filter(Boolean);
            
            // Extract modalities from pricingMetrics
            const allowedModalities = ['Credit', 'Usage', 'License'];
            const modalities = Array.isArray(company.pricingMetrics) 
              ? [...new Set(company.pricingMetrics
                  .filter(metric => metric?.modality && allowedModalities.includes(metric.modality))
                  .map(metric => metric.modality))]
              : [];

            return (
              <TableRow 
                key={company.slug}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => onCompanyClick(index)}
              >
                <TableCell>
                  {isInScrapbook && (
                    <BookmarkIcon className="h-4 w-4 text-blue-500" />
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {company.logoUrl && (
                      <img 
                        src={company.logoUrl} 
                        alt={`${company.slug} logo`} 
                        className="w-6 h-6 object-contain"
                      />
                    )}
                    {company.slug}
                  </div>
                </TableCell>
                <TableCell>{company.category}</TableCell>
                <TableCell>{formatEmployeeRange(company.employees)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {modalities.length > 0 ? (
                      modalities.slice(0, 3).map((modality, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {modality}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-gray-400 text-xs">None</span>
                    )}
                    {modalities.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{modalities.length - 3}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditClick(company.slug);
                    }}
                    className="h-8 w-8 p-0 hover:bg-blue-50"
                  >
                    <Edit3 className="h-4 w-4 text-blue-600" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default CompanyTableView;
